import { Check, Loader2, Mail, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  adminClient,
  type AdminStudioSeatCandidate,
  type AdminStudioSeatRole,
} from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { type AddPersonOutcome, isEmailAddress, summarizeAddPeople } from '@/lib/addPeople';
import { cn } from '@/lib/utils';

type Picked = { key: string; label: string; userId?: string; email?: string; joinOrganization?: boolean };

function CandidateAvatar({ person }: { person: AdminStudioSeatCandidate }) {
  if (person.picture) return <img src={person.picture} alt="" className="h-7 w-7 shrink-0 rounded-full bg-muted" />;
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
      {(person.displayName || person.email || '?').charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Give Studio access to people already in WorkOS, found by name or email, or
 * to anyone by email. A person with a login gets their seat at once, joining
 * the organization if they are outside it; only someone without one is
 * invited and gets access at their first sign-in.
 */
export function AddPeopleDialog({
  open,
  orgId,
  orgName,
  defaultRole,
  onClose,
  onAdded,
}: {
  open: boolean;
  orgId: string;
  orgName: string;
  defaultRole: AdminStudioSeatRole;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<AdminStudioSeatRole>(defaultRole);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [candidates, setCandidates] = useState<AdminStudioSeatCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const debouncedQuery = useDebouncedValue(query.trim(), 250);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPicked([]);
    setRole(defaultRole);
  }, [open, defaultRole]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    adminClient
      .listStudioSeatCandidates(orgId, debouncedQuery)
      .then((result) => {
        if (cancelled) return;
        setCandidates(result.candidates);
        setTotal(result.total);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId, debouncedQuery]);

  const pickedKeys = useMemo(() => new Set(picked.map((person) => person.key)), [picked]);
  const typedEmail = query.trim().toLowerCase();
  const offerEmail =
    isEmailAddress(typedEmail) &&
    !pickedKeys.has(`email:${typedEmail}`) &&
    !candidates.some((person) => person.email.toLowerCase() === typedEmail);

  const toggle = (person: Picked) =>
    setPicked((current) =>
      current.some((entry) => entry.key === person.key)
        ? current.filter((entry) => entry.key !== person.key)
        : [...current, person],
    );

  const pickEmail = () => {
    toggle({ key: `email:${typedEmail}`, label: typedEmail, email: typedEmail });
    setQuery('');
  };

  const submit = async () => {
    if (picked.length === 0) {
      toast.error('Pick at least one person');
      return;
    }
    setBusy(true);
    const outcomes: AddPersonOutcome[] = [];
    for (const person of picked) {
      try {
        const result = await adminClient.grantStudioSeat(
          orgId,
          person.userId
            ? { userId: person.userId, role, ...(person.joinOrganization ? { joinOrganization: true } : {}) }
            : { email: person.email, role },
        );
        outcomes.push({ label: person.label, result });
      } catch (error) {
        outcomes.push({ label: person.label, error: error instanceof Error ? error.message : 'Failed to grant access' });
      }
    }
    setBusy(false);
    const summary = summarizeAddPeople(outcomes);
    if (summary.added) toast.success(`${summary.added} to ${orgName}`);
    for (const line of summary.invited) toast.message(line);
    for (const line of summary.failed) toast.error(line);
    if (summary.failed.length === 0) onAdded();
    else setPicked((current) => current.filter((person) => outcomes.some((outcome) => outcome.label === person.label && outcome.error)));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add people"
      description={`Search anyone already in WorkOS by name or email, or type an email. Anyone with a login gets access right away, and joins ${orgName} if they are not in it yet. Anyone without one is invited and gets access at their first sign-in.`}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="add-people-form" disabled={busy || picked.length === 0}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {picked.length > 1 ? `Add ${picked.length} people` : 'Add'}
          </Button>
        </>
      }
    >
      <form
        id="add-people-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (offerEmail && picked.length === 0) {
            pickEmail();
            return;
          }
          void submit();
        }}
      >
        <div>
          <Label htmlFor="add-people-search">Name or email</Label>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              id="add-people-search"
              className="pl-9"
              type="text"
              autoComplete="off"
              placeholder="Search by name or email"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>

        {picked.length > 0 ? (
          <div className="flex flex-wrap gap-1.5" aria-label="People to add">
            {picked.map((person) => (
              <span key={person.key} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 py-0.5 pl-2.5 pr-1 text-xs text-foreground">
                {person.email && !person.userId ? <Mail className="h-3 w-3 text-muted-foreground" aria-hidden="true" /> : null}
                {person.label}
                <button
                  type="button"
                  onClick={() => toggle(person)}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                  aria-label={`Remove ${person.label}`}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          {offerEmail ? (
            <button
              type="button"
              onClick={pickEmail}
              className="flex w-full items-center gap-3 border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              </div>
              <span className="min-w-0 flex-1 truncate">
                Add <span className="font-medium">{typedEmail}</span>
              </span>
            </button>
          ) : null}
          {loading && candidates.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading people in {orgName}
            </div>
          ) : candidates.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              {debouncedQuery
                ? 'Nobody without access matches. Type their full email to add them.'
                : `Everyone in ${orgName} already has access. Search by name or email to add someone else.`}
            </p>
          ) : (
            <ul role="listbox" aria-multiselectable="true" aria-label="People without access">
              {candidates.map((person) => {
                const key = `user:${person.userId}`;
                const selected = pickedKeys.has(key);
                return (
                  <li key={person.userId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() =>
                        toggle({
                          key,
                          label: person.displayName || person.email || person.userId,
                          userId: person.userId,
                          joinOrganization: !person.inOrganization,
                        })
                      }
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary',
                        selected && 'bg-secondary',
                      )}
                    >
                      <CandidateAvatar person={person} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">{person.displayName || person.email}</span>
                        {person.displayName && person.email ? (
                          <span className="block truncate text-xs text-muted-foreground">{person.email}</span>
                        ) : null}
                      </span>
                      {person.inOrganization ? null : (
                        <span className="shrink-0 text-xs text-muted-foreground">Not in {orgName}</span>
                      )}
                      {selected ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {total > candidates.length ? (
            <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
              Showing {candidates.length} of {total}. Search to narrow the list.
            </p>
          ) : null}
        </div>

        <div>
          <Label htmlFor="add-people-role">Role</Label>
          <Select value={role} onValueChange={(next) => setRole(next as AdminStudioSeatRole)}>
            <SelectTrigger id="add-people-role" className="mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </form>
    </Dialog>
  );
}
